import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import Input from "@/components/base/Input";
import Button from "@/components/base/Button";
import { caseService } from "@/services/api";
import { mapDbCaseToFrontend } from "@/services/mapping";

export default function PsicologoCasosPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("todos");

  const [cases, setCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadCases = async () => {
    try {
      setLoading(true);
      setError("");
      // Filter category 'psicologico' (or category 'psicologia')
      const data = await caseService.list({ tipo: "psicologico" });
      if (Array.isArray(data)) {
        setCases(data.map(mapDbCaseToFrontend));
      }
    } catch (err: any) {
      console.error("Error loading psychology cases:", err);
      setError("Error al cargar casos: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases();
  }, []);

  const filtered = useMemo(() => {
    let list = [...cases];
    
    // Status Filter
    if (filter !== "todos") {
      list = list.filter(c => {
        if (filter === "critico") return c.prioridad === "critica";
        if (filter === "activo") return c.estado !== "cerrado";
        if (filter === "pendiente") return c.estado === "en_evaluacion" || c.estado === "esperando_insumos";
        return c.estado === filter;
      });
    }

    // Search query
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c => 
        c.folio.toLowerCase().includes(q) || 
        c.studentName.toLowerCase().includes(q) || 
        c.curso.toLowerCase().includes(q) || 
        c.titulo.toLowerCase().includes(q)
      );
    }

    return list;
  }, [cases, filter, search]);

  const handleExport = () => {
    const headers = ["Folio", "Estudiante", "Curso", "Fecha Derivación", "Prioridad", "Estado"];
    const rows = filtered.map(c => [
      c.folio,
      c.studentName,
      c.curso,
      c.fechaApertura,
      c.prioridad,
      c.estado
    ]);
    
    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Casos_Psicologia_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const getPriorityColor = (prio: string) => {
    switch (prio) {
      case "critica": return "danger";
      case "alta": return "warning";
      case "media": return "primary";
      default: return "neutral";
    }
  };

  const getStatusColor = (st: string) => {
    switch (st) {
      case "cerrado": return "neutral";
      case "en_evaluacion": return "warning";
      case "en_intervencion": return "primary";
      default: return "primary";
    }
  };

  const getPriorityLabel = (prio: string) => {
    switch (prio) {
      case "critica": return "Crítico";
      case "alta": return "Alto";
      case "media": return "Medio";
      default: return "Bajo";
    }
  };

  const getStatusLabel = (st: string) => {
    switch (st) {
      case "cerrado": return "Cerrado";
      case "en_evaluacion": return "En Evaluación";
      case "en_intervencion": return "En Intervención";
      default: return st.replace(/_/g, " ");
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Casos del Área Psicológica</h1>
          <p className="text-sm text-foreground-500 mt-0.5">Listado de estudiantes derivados para evaluación psicoterapéutica y acompañamiento psicosocial.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={handleExport} className="cursor-pointer">
            <i className="ri-download-line mr-1.5" /> Exportar
          </Button>
          <Button 
            onClick={() => navigate("/casos/nuevo")}
            size="sm" 
            className="cursor-pointer bg-primary-500 text-white"
          >
            <i className="ri-add-line mr-1.5" /> Agregar Registro
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <Input
          placeholder="Buscar por estudiante, RIT, curso o folio..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<i className="ri-search-line" />}
          className="max-w-sm"
          fullWidth={false}
        />
        <div className="flex gap-2 overflow-x-auto">
          {["todos", "activo", "pendiente", "critico"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold border cursor-pointer capitalize transition-all ${
                filter === f
                  ? "bg-foreground-900 text-white border-foreground-900"
                  : "bg-white text-foreground-600 border-background-200 hover:bg-background-50"
              }`}
            >
              {f === "todos" ? "Todos" : f === "critico" ? "⚠️ Críticos" : f}
            </button>
          ))}
        </div>
      </div>

      <Card padding="none">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-12 text-center text-foreground-500">
              <i className="ri-loader-4-line animate-spin mr-2" /> Cargando casos...
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-50/50">
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Folio</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Estudiante</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Curso</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Fecha Derivación</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Nivel de Riesgo</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Estado</th>
                  <th className="text-right py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                    <td className="py-3.5 px-4 font-semibold text-foreground-900 whitespace-nowrap">
                      {c.folio}
                    </td>
                    <td className="py-3.5 px-4 text-foreground-800 font-medium whitespace-nowrap text-xs">
                      {c.studentName}
                    </td>
                    <td className="py-3.5 px-4 text-foreground-600 whitespace-nowrap text-xs">
                      {c.curso}
                    </td>
                    <td className="py-3.5 px-4 text-foreground-600 whitespace-nowrap text-xs">
                      {c.fechaApertura}
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <Badge variant={getPriorityColor(c.prioridad)}>{getPriorityLabel(c.prioridad)}</Badge>
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <Badge variant={getStatusColor(c.estado)}>{getStatusLabel(c.estado)}</Badge>
                    </td>
                    <td className="py-3.5 px-4 text-right whitespace-nowrap">
                      <button 
                        onClick={() => navigate(`/casos/${c.id}`)}
                        className="text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer p-1"
                        title="Ver detalles"
                      >
                        <i className="ri-eye-line text-base" />
                      </button>
                      <button 
                        onClick={() => navigate(`/casos/${c.id}`)}
                        className="text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer p-1 ml-1.5"
                        title="Editar"
                      >
                        <i className="ri-edit-line text-base" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-foreground-400">
                      No se encontraron registros que coincidan con la búsqueda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}
